// ==========================================
// FILE: frontend/src/components/AdminDashboard.jsx
// ==========================================
import React, { useState, useEffect } from 'react';
import { Search, ExternalLink, Calendar, X, Eye, AlertCircle, Loader, RefreshCw, Filter, Trash2, LogOut, ShieldCheck } from 'lucide-react';
import { apiService } from '../../services/api.service'; // Centralized service
import { ROUTES } from '../../config/api.config'; // Centralized config

const AdminDashboard = () => {
  // State
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // 1. SECURITY: Auth Guard
  useEffect(() => {
    if (!apiService.isAuthenticated()) {
      window.location.href = ROUTES.LOGIN;
    } else {
      fetchSubmissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // 2. DATA: Fetching
  const fetchSubmissions = async () => {
    setLoading(true);
    setError('');
    
    try {
      const endpoint = statusFilter 
        ? `/admin/submissions?status=${statusFilter}`
        : `/admin/submissions`;

      const response = await apiService.get(endpoint);

      if (response.success) {
        setSubmissions(response.data?.submissions || []);
      } else {
        setError(response.error || 'Failed to load submissions');
      }
    } catch (err) {
      console.error(err);
      setError('Connection error. Unable to reach the server.');
    } finally {
      setLoading(false);
    }
  };

  // 3. ACTION: Update Status
  const updateStatus = async (id, newStatus) => {
    setUpdating(true);
    setError('');
    
    try {
      const response = await apiService.patch(`/admin/submissions/${id}/status`, { 
        status: newStatus 
      });

      if (response.success) {
        // Refresh list to reflect changes
        await fetchSubmissions();
        
        // Update local modal state if open
        if (selectedSubmission && selectedSubmission.id === id) {
          setSelectedSubmission({ ...selectedSubmission, status: newStatus });
        }
      } else {
        setError(response.error || 'Failed to update status');
      }
    } catch (err) {
      setError(err.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  // 4. ACTION: Delete
  const handleDeleteSubmission = async (id) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to permanently delete this submission and all its images?'
    );
    if (!confirmDelete) return;

    setUpdating(true);
    setError('');

    try {
      const response = await apiService.delete(`/admin/submissions/${id}`);

      if (response.success) {
        setSubmissions(prev => prev.filter(s => s.id !== id));
        setSelectedSubmission(null); // Close modal
      } else {
        setError(response.error || 'Failed to delete submission');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete submission.');
    } finally {
      setUpdating(false);
    }
  };

  const handleLogout = () => {
    apiService.logout();
    window.location.href = ROUTES.LOGIN;
  };

  // Filter logic (Frontend side search)
  const filteredSubmissions = submissions.filter(sub => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (sub.id && sub.id.toLowerCase().includes(q)) ||
      (sub.username && sub.username.toLowerCase().includes(q))
    );
  });

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 pt-20 md:pt-24 px-4 md:px-8 font-sans pb-12 relative">
      
      {/* Decorative Background */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-violet-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-600 rounded-lg text-white shadow-lg">
                    <ShieldCheck size={24} />
                </div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-800 via-blue-600 to-purple-600 bg-clip-text text-transparent">
                Admin Dashboard
                </h1>
            </div>
            <p className="text-gray-600 text-lg">Monitor and manage user feedback submissions</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto flex-wrap">
            {/* Search Bar */}
            <div className="flex items-center gap-3 bg-white/90 backdrop-blur-xl p-3 rounded-2xl border-2 border-gray-200 w-full md:w-auto shadow-sm focus-within:border-cyan-400 transition-colors">
              <Search className="text-gray-400 ml-2" size={20} />
              <input 
                type="text" 
                placeholder="Search User or ID..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-none focus:outline-none text-gray-900 placeholder-gray-400 w-full md:w-48 lg:w-64"
              />
            </div>

            {/* Filter Dropdown */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 bg-white/90 backdrop-blur-xl border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-cyan-400 text-gray-900 font-semibold shadow-sm cursor-pointer"
            >
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>

            {/* Refresh Button */}
            <button
              onClick={fetchSubmissions}
              disabled={loading}
              className="px-4 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-2xl font-bold shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 transition-all hover:scale-105"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              <span className="hidden lg:inline">Refresh</span>
            </button>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="px-4 py-3 bg-red-50 hover:bg-red-100 text-red-600 border-2 border-red-200 rounded-2xl font-bold shadow-sm flex items-center justify-center gap-2 transition-colors"
              title="Logout"
            >
              <LogOut size={18} />
              <span className="hidden lg:inline">Logout</span>
            </button>
          </div>
        </div>

        {/* Global Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-2xl flex items-start gap-3 animate-shake">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-red-700 text-sm font-medium">{error}</p>
          </div>
        )}

        {/* Content Area */}
        {loading && submissions.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="animate-spin text-cyan-600" size={48} />
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <div className="text-center py-20 bg-white/60 backdrop-blur-xl rounded-3xl border-2 border-dashed border-gray-300">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-gray-100 text-gray-400 mb-4">
              <Filter size={48} strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">No Submissions Found</h3>
            <p className="text-gray-500">Try adjusting your filters or search query.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSubmissions.map((sub) => (
              <div
                key={sub.id}
                onClick={() => setSelectedSubmission(sub)}
                className="bg-white/90 backdrop-blur-xl border-2 border-gray-100 hover:border-cyan-400 rounded-2xl p-6 transition-all hover:shadow-xl group cursor-pointer transform hover:-translate-y-1 duration-200"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full shadow-sm ${
                      sub.status === 'open' ? 'bg-red-500' : 
                      sub.status === 'resolved' ? 'bg-emerald-500' : 
                      'bg-gray-500'
                    }`} />
                    <span className="text-xs font-mono text-gray-400 truncate max-w-[100px]">#{sub.id.slice(-6)}</span>
                  </div>
                  <span className={`px-3 py-1 text-xs font-bold rounded-lg border ${
                    sub.status === 'open' ? 'bg-red-50 text-red-600 border-red-200' : 
                    sub.status === 'resolved' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 
                    'bg-gray-50 text-gray-600 border-gray-200'
                  }`}>
                    {sub.status?.toUpperCase()}
                  </span>
                </div>
                
                <h3 className="text-xl font-bold text-gray-800 mb-3 truncate">{sub.username}</h3>
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-6">
                  <Calendar size={16} />
                  {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : 'N/A'}
                </div>

                <div className="space-y-2 mb-6 p-4 bg-gray-50 rounded-xl">
                  <div className="text-sm text-gray-600 flex justify-between">
                    <span>Issues Reported:</span>
                    <span className="text-cyan-600 font-bold">{sub.fields?.length || 0}</span>
                  </div>
                  <div className="text-sm text-gray-600 flex justify-between">
                    <span>Attachments:</span>
                    <span className="text-violet-600 font-bold">
                      {sub.fields?.reduce((acc, curr) => acc + (curr.images?.length || 0), 0) || 0}
                    </span>
                  </div>
                </div>

                <button 
                  className="w-full py-3 bg-white text-cyan-700 border-2 border-cyan-100 group-hover:border-cyan-500 group-hover:bg-cyan-50 rounded-xl transition-all flex items-center justify-center gap-2 font-bold"
                >
                  <Eye size={20} />
                  Inspect Details
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- MODAL --- */}
      {selectedSubmission && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center px-4 bg-black/60 backdrop-blur-md transition-all">
          <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col animate-in fade-in zoom-in duration-200">
            
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  Submission Details
                  <span className="text-xs font-normal font-mono text-cyan-700 px-2 py-1 bg-cyan-100 rounded-lg">
                    {selectedSubmission.id}
                  </span>
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  Submitted by <span className="font-bold text-gray-900">{selectedSubmission.username}</span>
                </p>
              </div>
              <button 
                onClick={() => setSelectedSubmission(null)}
                className="p-2 hover:bg-gray-200 text-gray-500 hover:text-gray-700 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body (Scrollable) */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-white">
              {selectedSubmission.fields?.map((field, index) => (
                <div key={index} className="bg-gray-50 border border-gray-200 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-white p-2 rounded-lg border border-violet-200 text-violet-600 shadow-sm">
                      <AlertCircle size={20} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">{field.name}</h3>
                  </div>

                  <div className="mb-4 pl-4 border-l-2 border-violet-300">
                    <p className="text-gray-700 leading-relaxed font-medium whitespace-pre-wrap">
                      {field.text || 'No description provided'}
                    </p>
                  </div>

                  {field.link && (
                    <div className="mb-4 pl-4 border-l-2 border-cyan-300">
                      <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-1 font-bold">Reference URL</h4>
                      <a 
                        href={field.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 hover:underline break-all font-medium text-sm"
                      >
                        <ExternalLink size={14} />
                        {field.link}
                      </a>
                    </div>
                  )}

                  {field.images && field.images.length > 0 && (
                    <div className="pl-4 border-l-2 border-gray-300">
                      <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-3 font-bold">Evidence Attached</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {field.images.map((img, i) => (
                          <div key={i} className="relative group rounded-lg overflow-hidden border border-gray-200 aspect-square bg-gray-100 cursor-zoom-in">
                             {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                            <img 
                              src={img} 
                              alt={`Evidence ${i}`} 
                              className="w-full h-full object-cover transition-transform group-hover:scale-105" 
                            />
                            <a 
                              href={img} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                            >
                              <Eye size={24} />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-4">
              <button
                onClick={() => handleDeleteSubmission(selectedSubmission.id)}
                disabled={updating}
                className="w-full sm:w-auto px-6 py-3 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
              >
                <Trash2 size={18} />
                {updating ? 'Deleting...' : 'Delete Submission'}
              </button>

              <div className="flex gap-3 w-full sm:w-auto">
                <select
                  value={selectedSubmission.status}
                  onChange={(e) => updateStatus(selectedSubmission.id, e.target.value)}
                  disabled={updating}
                  className="flex-1 sm:flex-none px-6 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:border-cyan-500 text-gray-900 font-bold disabled:opacity-50 shadow-sm"
                >
                  <option value="open">Open</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>

                <button 
                  onClick={() => setSelectedSubmission(null)}
                  className="px-6 py-3 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-xl font-bold shadow-sm transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;